import { all, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';
import equal from 'fast-deep-equal';
import { v4 as uuidv4 } from 'uuid';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IChecklistTemplate } from 'APIPackage/AccountModule/interfaces/IChecklistTemplate';
import { IChecklistTemplateQuestion } from 'APIPackage/AccountModule/interfaces/IChecklistTemplateQuestion';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { accountChecklistTemplatesGet } from 'CorePackage/AccountModule/actions/accountActions';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { IPageChecklistTemplate } from 'APIPackage/AccountModule/interfaces/IPageChecklistTemplate';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { addNoticeAction, removeAllNoticesAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import {
  fetchChecklistExport,
  fetchChecklistTemplate,
  fetchChecklistTemplateDelete,
  fetchChecklistTemplateSetDefault,
  fetchChecklistTemplateUpdate,
} from 'APIPackage/AccountModule/services/checklistTemplatesApi';
import { IChecklistTemplateUpdate } from 'APIPackage/AccountModule/interfaces/IChecklistTemplateUpdate';
import {
  CHECKLIST_TEMPLATE_CHANGES_WARNING,
  CHECKLIST_TEMPLATE_DEFAULT_IMPOSSIBLE_WARNING,
  CHECKLIST_TEMPLATE_DEFAULT_WARNING,
  CHECKLIST_TEMPLATE_DELETE_SUCCESSFULLY,
  CHECKLIST_TEMPLATE_UPDATE_ERROR_DUPLICATE,
  CHECKLIST_TEMPLATE_UPDATE_SUCCESSFULLY,
} from 'CorePackage/MainModule/constants/notices';
import { CHECKLIST_TEMPLATE_ERROR_DUPLICATE_MESSAGE_PART } from 'CorePackage/MainModule/constants/httpErrors';
import { getAccountOrganizationsData } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import { CheckListsActionsEnum } from '../enums/CheckListsActionsEnum';
import { initialCheckListTemplatesParams } from '../constants/initialCheckListTemplatesParams';
import {
  getCheckListsActiveTemplateId,
  getCheckListsOrder,
  getCheckListsPage,
  getCheckListsPageCount,
  getCheckListsTemplateList,
  getCheckListsTemplates,
  getCheckListsTotalCount,
} from '../selectors/checkListsSelectors';
import {
  checkListsChangeDefault,
  checkListsDelete,
  checkListsExportById,
  checkListsFormQuestionDelete,
  checkListsFormSubmit,
  checkListsSetActiveTemplateId,
  checkListsSetTemplateList,
  checkListsUpdate,
} from '../actions/checkListsAction';
import { USER_CHECKLIST_PAGE_SIZE } from '../constants/userChecklistPageSize';
import { CheckListQuestionDeleteNoticeContent } from '../components/CheckListQuestionDeleteNoticeContent';

const sortList = (list: Array<IChecklistTemplate>) => {
  let newChecklistTemplatesList: Array<IChecklistTemplate> = [];

  if (list.length) {
    newChecklistTemplatesList = list.map(template => {
      return {
        ...template,
        questions: [...(template.questions || [])].sort(
          (a: IChecklistTemplateQuestion, b: IChecklistTemplateQuestion) => {
            const indexA = a.index;
            const indexB = b.index;

            if (typeof indexA === 'number' && typeof indexB === 'number') {
              return indexA - indexB;
            }
            if (!indexA) {
              return 1;
            }
            if (!indexB) {
              return -1;
            }

            return 0;
          },
        ),
      };
    });
    newChecklistTemplatesList = newChecklistTemplatesList.sort((a, b) => {
      const idA = a.id;
      const idB = b.id;

      if (typeof idA === 'number' && typeof idB === 'number') {
        return idB - idA;
      }
      if (!idA) {
        return -1;
      }
      if (!idB) {
        return 1;
      }

      return 0;
    });
  }

  return newChecklistTemplatesList;
};

const commonCatalogsNames: Array<CommonCatalogsNamesEnum> = [CommonCatalogsNamesEnum.USER_KNOS];

export function* watchCheckListsSaga() {
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_INIT_PAGE, checkListsInitPageSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_GET_TEMPLATE_LIST, checkListsGetTemplateListSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_GET_TEMPLATE_LIST_NEXT_PAGE, checkListsGetTemplateListNextPageSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_SET_ACTIVE_TEMPLATE_ID, checkListsSetActiveTemplateIdSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_UPDATE, checkListsUpdateSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_DELETE, checkListsDeleteSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_CHANGE_DEFAULT, checkListsChangeDefaultSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_EXPORT_BY_ID, checkListsExportByIdSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_FORM_SUBMIT, checkListsFormSubmitSaga);
  yield takeEvery(CheckListsActionsEnum.CHECK_LISTS_FORM_QUESTION_DELETE, checkListsFormQuestionDeleteSaga);
}

function* checkListsInitPageSaga() {
  yield all([...commonCatalogsNames.map(catalogName => put(catalogGet(catalogName)))]);
  yield put(accountChecklistTemplatesGet(initialCheckListTemplatesParams));
}

function* checkListsGetTemplateListSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const templates = getCheckListsTemplates(state);
    const templateList = getCheckListsTemplateList(state);

    if (templates?.data) {
      const res: IPageChecklistTemplate = templates?.data;
      const newTemplateList = res.list.filter(newItem => !templateList.find(item => item.id === newItem.id));
      const filteredList = sortList([...templateList, ...newTemplateList]);

      yield put(checkListsSetTemplateList(filteredList, res.page, res.pageCount, res.totalCount));
      yield put(checkListsSetActiveTemplateId(filteredList[0]?.id));
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.GET_TEMPLATES,
        }),
      );
    },
  });
}

function* checkListsGetTemplateListNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order = getCheckListsOrder(state);
    const page = getCheckListsPage(state);
    const totalCount = getCheckListsTotalCount(state);
    const pageCount = getCheckListsPageCount(state);
    const templateList = getCheckListsTemplateList(state);

    yield put(
      accountChecklistTemplatesGet({
        isCaching: false,
        order,
        page: (page + 1).toString(),
        size: USER_CHECKLIST_PAGE_SIZE,
      }),
    );
    yield put(checkListsSetTemplateList(templateList, page + 1, pageCount, totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* checkListsSetActiveTemplateIdSaga(action: ActionType<typeof checkListsSetActiveTemplateId>) {
  function* funcTry() {
    const { id } = action.payload;

    if (typeof id !== 'number') {
      return;
    }
    const state: IRootState = yield select();
    const templatesList = getCheckListsTemplateList(state);
    const page = getCheckListsPage(state);
    const pageCount = getCheckListsPageCount(state);
    const totalCount = getCheckListsTotalCount(state);

    const checkListTemplate: IChecklistTemplate = yield fetchChecklistTemplate(id);

    if (checkListTemplate) {
      yield put(
        checkListsSetTemplateList(
          templatesList.map(item => {
            if (item.id === checkListTemplate.id) {
              return {
                ...checkListTemplate,
                byDefault: item.byDefault,
              };
            }

            return item;
          }),
          page,
          pageCount,
          totalCount,
        ),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* checkListsUpdateSaga(action: ActionType<typeof checkListsUpdate>) {
  function* funcTry() {
    const { values } = action.payload;
    const { templateName: name, approvalDetails, organisation, questions } = values;
    const state: IRootState = yield select();
    const templatesList = getCheckListsTemplateList(state);
    const activeTemplateId = getCheckListsActiveTemplateId(state);
    const activeTemplate = templatesList.find(item => item.id === activeTemplateId);
    const page = getCheckListsPage(state);
    const pageCount = getCheckListsPageCount(state);
    const totalCount = getCheckListsTotalCount(state);
    const knoOrganizationId = organisation?.id;

    if (knoOrganizationId) {
      const data: IChecklistTemplateUpdate = {
        id: activeTemplate?.id || undefined,
        name,
        approvalDetails,
        byDefault: activeTemplate?.byDefault || false,
        knoOrganizationId: knoOrganizationId || '',
        questions: questions.map((item, index) => {
          const { id: qId, question, npaProps } = item;

          return {
            id: qId,
            question,
            npaProps,
            index,
          };
        }),
      };
      const response: IChecklistTemplate = yield fetchChecklistTemplateUpdate({ data });
      const newTemplatesList = () => {
        let prevItems = templatesList;
        const isItemExist = prevItems.some(item => item.id === response.id);

        if (response.byDefault) {
          prevItems = prevItems.map(item => {
            return {
              ...item,
              byDefault: false,
            };
          });
        }
        if (!isItemExist) {
          return [...prevItems, response];
        }

        return prevItems.map(item => {
          if (item.id === response.id) {
            return response;
          }

          return item;
        });
      };

      yield put(checkListsSetTemplateList(newTemplatesList(), page, pageCount, totalCount));
      yield put(checkListsSetActiveTemplateId(response.id));
      yield put(
        addNoticeAction({
          renderContent: CHECKLIST_TEMPLATE_UPDATE_SUCCESSFULLY,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.UPDATE_TEMPLATE,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      const { message, text } = error;

      yield put(
        addNoticeAction({
          renderContent:
            message && message.indexOf(CHECKLIST_TEMPLATE_ERROR_DUPLICATE_MESSAGE_PART) >= 0
              ? CHECKLIST_TEMPLATE_UPDATE_ERROR_DUPLICATE
              : text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.UPDATE_TEMPLATE,
        }),
      );
    },
  });
}

function* checkListsDeleteSaga(action: ActionType<typeof checkListsDelete>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const templatesList = getCheckListsTemplateList(state);
    const page = getCheckListsPage(state);
    const pageCount = getCheckListsPageCount(state);
    const totalCount = getCheckListsTotalCount(state);
    const activeTemplateId = getCheckListsActiveTemplateId(state);

    const response: IChecklistTemplate = yield fetchChecklistTemplateDelete(id);

    yield put(
      checkListsSetTemplateList(
        templatesList.filter(item => item.id !== response.id),
        page,
        pageCount,
        totalCount,
      ),
    );
    if (activeTemplateId === id) {
      yield put(checkListsSetActiveTemplateId(templatesList.find(item => item.id !== id)?.id || 'new'));
    }
    yield put(
      addNoticeAction({
        renderContent: CHECKLIST_TEMPLATE_DELETE_SUCCESSFULLY,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.DELETE_TEMPLATE,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DELETE_TEMPLATE,
        }),
      );
    },
  });
}

function* checkListsChangeDefaultSaga(action: ActionType<typeof checkListsChangeDefault>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const templatesList = getCheckListsTemplateList(state);
    const knoOrganisations = getAccountOrganizationsData(state).kno;
    const page = getCheckListsPage(state);
    const pageCount = getCheckListsPageCount(state);
    const totalCount = getCheckListsTotalCount(state);

    if (id) {
      const template = templatesList.find(tId => tId.id === id);

      if (template) {
        const { knoOrganizationId } = template;
        const defaultKnoOrganisationId = knoOrganisations.find(item => item.byDefault)?.id;

        if (knoOrganizationId && knoOrganizationId === defaultKnoOrganisationId) {
          const changeDefaultTemplateResponse: IChecklistTemplate = yield fetchChecklistTemplateSetDefault(id);

          if (changeDefaultTemplateResponse) {
            const newTemplatesList = [...templatesList];
            const oldTemplateDefaultIndex = templatesList.findIndex(item => item.byDefault);

            if (oldTemplateDefaultIndex >= 0) {
              newTemplatesList[oldTemplateDefaultIndex] = {
                ...newTemplatesList[oldTemplateDefaultIndex],
                byDefault: false,
              };
            }
            newTemplatesList[newTemplatesList.findIndex(item => item.id === template.id)] = {
              ...template,
              byDefault: true,
            };
            yield put(checkListsSetTemplateList(newTemplatesList, page, pageCount, totalCount));

            yield put(
              addNoticeAction({
                renderContent: `Проверочный лист "${changeDefaultTemplateResponse.name}" выбран по умолчанию`,
                noticeType: NoticeTypesEnum.SUCCESS,
                messageType: MessageTypesEnum.SELECT_CHECKLIST,
              }),
            );
          }
        } else {
          yield put(
            addNoticeAction({
              renderContent: CHECKLIST_TEMPLATE_DEFAULT_IMPOSSIBLE_WARNING,
              noticeType: NoticeTypesEnum.WARNING,
              messageType: MessageTypesEnum.DEFAULT_IMPOSSIBLE_TEMPLATE,
            }),
          );
        }
      }
    } else {
      addNoticeAction({
        renderContent: CHECKLIST_TEMPLATE_DEFAULT_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.DEFAULT_TEMPLATE,
      });
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.CHANGE_DEFAULT_TEMPLATE,
        }),
      );
    },
  });
}

function* checkListsExportByIdSaga(action: ActionType<typeof checkListsExportById>) {
  function* funcTry() {
    const { id, format, successRenderContent } = action.payload;
    const checkListTemplate: IExport = yield fetchChecklistExport({ id, format });

    yield put(
      addNoticeAction({
        renderContent: successRenderContent(checkListTemplate.id),
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.EXPORT,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error) {
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.EXPORT_CHECKLIST,
        }),
      );
    },
  });
}

function* checkListsFormSubmitSaga(action: ActionType<typeof checkListsFormSubmit>) {
  const { values, initialValues, onSubmit, resetForm } = action.payload;

  const isFormChange = !equal(
    { ...values, questions: values.questions.filter(item => !item.deleteId) },
    { ...initialValues, questions: initialValues.questions },
  );

  if (isFormChange && onSubmit) {
    yield put(removeAllNoticesAction());
    onSubmit({
      ...values,
      questions: values.questions
        .filter(item => !item.deleteId)
        .map((item, index) => ({
          id: item.id,
          question: item.question,
          npaProps: item.npaProps,
          index,
        })),
    });
    if (resetForm) {
      resetForm();
    }
  } else {
    yield put(
      addNoticeAction({
        renderContent: CHECKLIST_TEMPLATE_CHANGES_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.SAVE_CHECKLIST_CHANGES,
      }),
    );
  }
}

function* checkListsFormQuestionDeleteSaga(action: ActionType<typeof checkListsFormQuestionDelete>) {
  const { name, onUndo } = action.payload;
  const id = uuidv4();

  yield put(
    addNoticeAction({
      renderContent: <CheckListQuestionDeleteNoticeContent name={name} id={id} onUndo={onUndo} />,
      noticeType: NoticeTypesEnum.WARNING,
      messageType: MessageTypesEnum.DELETE_SECURITY_QUESTION,
      id,
    }),
  );
}
